/**
 * PATH: packages/quote-builder/src/formula/evaluate.ts
 * PURPOSE: The shared client-side formula evaluator for the unified quote
 *          builder. Evaluates Excel-style "=expression" cell formulas (or
 *          bare numeric literals) against a numeric context.
 *
 * Mirrors the backend evaluator in
 * quotech-backend/quotebuilder/services/formula_eval.py — identical
 * function whitelist (SUM / AVG / MIN / MAX / ABS / ROUND / CEIL / FLOOR),
 * identical "unknown identifier resolves to 0" semantics — so a formula
 * stored in a QuoteBuilderProfile evaluates the same on both sides.
 *
 * Lifted from src/features/bravada-quotes/lib/formula.ts (which now
 * re-exports from here) so SAM / Newmark / Bravada all run ONE evaluator.
 */

export type FormulaContext = Record<string, number>

/** A token the user can reference in a formula (drives autocomplete). */
export interface FormulaToken {
  token: string
  detail: string
}

const FUNCTION_NAMES = ['SUM', 'AVG', 'MIN', 'MAX', 'ABS', 'ROUND', 'CEIL', 'FLOOR']

const HELPERS: Record<string, (...args: number[]) => number> = {
  SUM: (...v) => v.reduce((a, b) => a + (Number(b) || 0), 0),
  AVG: (...v) => (v.length ? v.reduce((a, b) => a + (Number(b) || 0), 0) / v.length : 0),
  MIN: (...v) => (v.length ? Math.min(...v) : 0),
  MAX: (...v) => (v.length ? Math.max(...v) : 0),
  ABS: (x = 0) => Math.abs(Number(x) || 0),
  ROUND: (x = 0, d = 0) => {
    const m = Math.pow(10, Number(d) || 0)
    return Math.round((Number(x) || 0) * m) / m
  },
  CEIL: (x = 0) => Math.ceil(Number(x) || 0),
  FLOOR: (x = 0) => Math.floor(Number(x) || 0),
}

const BARE_NUMBER_RE = /^[-+]?(?:\d+\.?\d*|\.\d+)$/
const ALLOWED_RE = /^[A-Za-z0-9_.,+\-*/%()\s]*$/
const IDENT_RE = /[A-Za-z_][A-Za-z0-9_]*/g

/** True when the text is an Excel-style "=expression" (vs. a manual literal). */
export function isFormula(text: string | null | undefined): boolean {
  return typeof text === 'string' && text.trim().startsWith('=')
}

/**
 * Evaluate a cell expression (or bare numeric literal) against a numeric
 * context. Returns `null` when blank or not evaluable to a finite number
 * (caller then falls back to the default formula).
 */
export function evaluateFormula(
  expr: string | null | undefined,
  context: FormulaContext,
): number | null {
  if (expr == null) return null
  let text = String(expr).trim()
  if (!text) return null
  if (text.startsWith('=')) text = text.slice(1).trim()
  if (!text) return null

  // Fast path: a bare number (manual literal).
  if (BARE_NUMBER_RE.test(text)) {
    const n = Number(text)
    return Number.isFinite(n) ? n : null
  }

  if (!ALLOWED_RE.test(text)) return null

  // Normalise function names to upper-case (so `sum(` works like `SUM(`).
  let normalised = text
  for (const fn of FUNCTION_NAMES) {
    normalised = normalised.replace(new RegExp(`\\b${fn}\\s*\\(`, 'gi'), `${fn}(`)
  }

  // Collect identifiers that are not function calls; unknown ones resolve to 0.
  const params: Record<string, number> = {}
  let m: RegExpExecArray | null
  IDENT_RE.lastIndex = 0
  while ((m = IDENT_RE.exec(normalised)) !== null) {
    const name = m[0]
    if (FUNCTION_NAMES.includes(name)) continue
    const after = normalised.slice(m.index + name.length).trimStart()
    if (after.startsWith('(')) continue // a function-like call we don't support
    if (!(name in params)) {
      const v = context[name]
      params[name] = typeof v === 'number' && Number.isFinite(v) ? v : 0
    }
  }

  const paramNames = Object.keys(params)
  const helperNames = Object.keys(HELPERS)
  try {
    const fn = new Function(
      ...paramNames,
      ...helperNames,
      `"use strict"; return (${normalised});`,
    )
    const result = fn(
      ...paramNames.map((p) => params[p]),
      ...helperNames.map((h) => HELPERS[h]),
    )
    return typeof result === 'number' && Number.isFinite(result) ? result : null
  } catch {
    return null
  }
}

/**
 * Evaluate a profile's custom columns over a line context — the FE mirror
 * of CalculationEngine.evaluate_custom_columns. `calc` columns run their
 * formula and feed the result back into the context so later columns can
 * reference earlier ones (DAG by sort order).
 */
export function evaluateCustomColumns(
  columns: Array<{
    key: string
    kind?: string
    value_type?: string
    default_value?: string | number | null
    formula?: string | null
    sort_order?: number
  }>,
  context: Record<string, number | string | null | undefined>,
): Record<string, number | string | null> {
  const results: Record<string, number | string | null> = {}
  const ctx: FormulaContext = {}
  for (const [key, value] of Object.entries(context)) {
    const n = typeof value === 'number' ? value : Number(value)
    ctx[key] = Number.isFinite(n) ? n : 0
  }

  const sorted = [...(columns || [])]
    .filter((c) => c && typeof c.key === 'string' && c.key.trim())
    .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0) || a.key.localeCompare(b.key))

  for (const col of sorted) {
    const key = col.key.trim()
    if (col.kind === 'calc') {
      const value = evaluateFormula(col.formula ?? null, ctx)
      results[key] = value
      ctx[key] = value ?? 0
      continue
    }
    const raw = context[key] ?? col.default_value ?? null
    if (col.value_type === 'text') {
      results[key] = raw == null ? null : String(raw)
      continue
    }
    const n = raw == null || raw === '' ? null : Number(raw)
    results[key] = n != null && Number.isFinite(n) ? n : null
    ctx[key] = n != null && Number.isFinite(n) ? n : 0
  }

  return results
}
